import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { sessionState } from '@chainlit/react-client';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { reportFormState } from '@/state/project';

type ContextFile = {
  name: string;
  type: string;
  content: string;
};

const MAX_FILE_BYTES = 5 * 1024 * 1024;

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => resolve('');
    reader.readAsText(file);
  });
}

function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => resolve('');
    reader.readAsDataURL(file);
  });
}

export default function ReportFormDialog() {
  const [payload, setPayload] = useRecoilState(reportFormState);
  const session = useRecoilValue(sessionState);
  const [template, setTemplate] = useState('default');
  const [format, setFormat] = useState('pdf');
  const [advanced, setAdvanced] = useState(true);
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<ContextFile[]>([]);
  const [templateFile, setTemplateFile] = useState<ContextFile | null>(null);

  useEffect(() => {
    if (payload) {
      setTemplate(payload.current.template || 'default');
      setFormat(payload.current.output_format || 'pdf');
      setAdvanced(payload.current.advanced ?? true);
      setNotes(payload.current.notes || '');
      setFiles([]);
      setTemplateFile(null);
    }
  }, [payload]);

  if (!payload) return null;

  const onPickFiles = async (list: FileList | null) => {
    if (!list) return;
    const accepted: ContextFile[] = [];
    for (const file of Array.from(list).slice(0, payload.max_files || 5)) {
      if (file.size > MAX_FILE_BYTES) continue;
      const content = await readFileAsText(file);
      if (content) accepted.push({ name: file.name, type: file.type, content });
    }
    setFiles((prev) => [...prev, ...accepted]);
  };

  const onPickTemplate = async (list: FileList | null) => {
    const file = list?.[0];
    if (!file || file.size > MAX_FILE_BYTES) {
      setTemplateFile(null);
      return;
    }
    const isPdf =
      file.type.includes('pdf') || file.name.toLowerCase().endsWith('.pdf');
    const content = isPdf
      ? await readFileAsDataURL(file)
      : await readFileAsText(file);
    if (content) setTemplateFile({ name: file.name, type: file.type, content });
  };

  const emit = (action: 'generate' | 'cancel') => {
    const socket = session?.socket;
    if (socket && typeof socket.emit === 'function') {
      socket.emit('report_form_response', {
        form_id: payload.form_id,
        action,
        template,
        output_format: format,
        advanced,
        notes,
        files,
        template_file: template === 'upload' ? templateFile : null
      });
    }
    setPayload(null);
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && emit('cancel')}>
      <DialogContent className="w-[92vw] max-w-[720px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate Report</DialogTitle>
          <DialogDescription>
            Configure and generate the penetration-test report.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="report-standard">Report Standard</Label>
            <Select value={template} onValueChange={setTemplate}>
              <SelectTrigger id="report-standard">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {payload.standards.map((choice) => (
                  <SelectItem key={choice.id} value={choice.id}>
                    {choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {template === 'upload' && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="report-template-file">Custom Template File</Label>
              <input
                id="report-template-file"
                type="file"
                accept=".md,.pdf"
                onChange={(e) => onPickTemplate(e.target.files)}
                className="text-sm"
              />
              {templateFile && (
                <div className="flex w-fit items-center gap-1 rounded bg-muted px-2 py-1 text-sm">
                  <span>{templateFile.name}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4"
                    onClick={() => setTemplateFile(null)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="report-format">Output Format</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger id="report-format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {payload.formats.map((value) => (
                  <SelectItem key={value} value={value}>
                    {value === 'all'
                      ? 'All formats (.zip)'
                      : value.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="report-advanced"
              checked={advanced}
              onCheckedChange={(checked) => setAdvanced(checked === true)}
            />
            <Label htmlFor="report-advanced" className="font-normal">
              Advanced report (intent · tool narrative · control mapping)
            </Label>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="report-notes">Notes</Label>
            <Textarea
              id="report-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Operational context to weave into the report…"
              rows={3}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="report-files">Context Files</Label>
            <input
              id="report-files"
              type="file"
              multiple
              accept={payload.accepted_file_types.join(',')}
              onChange={(e) => onPickFiles(e.target.files)}
              className="text-sm"
            />
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {files.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center gap-1 rounded bg-muted px-2 py-1 text-sm"
                  >
                    <span>{file.name}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4"
                      onClick={() =>
                        setFiles((prev) => prev.filter((_, i) => i !== index))
                      }
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex sm:justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => emit('cancel')}
          >
            Cancel
          </Button>
          <Button type="button" onClick={() => emit('generate')}>
            Generate Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
